import { EnvironmentInjector, Injectable, runInInjectionContext } from '@angular/core';
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';

/**
 * Caches Firebase Storage download URLs so we don't call getDownloadURL repeatedly
 * (same cost as a Storage read for billing; dev hot-reloads were multiplying calls).
 * sessionStorage survives tab reloads during ng serve; memory handles same-session reuse.
 */
@Injectable({
  providedIn: 'root'
})
export class StorageUrlCacheService {
  private readonly memory = new Map<string, string>();
  private readonly inflight = new Map<string, Promise<string>>();
  private readonly sessionKeyPrefix = 'fsDL:';

  constructor(
    private readonly _storage: Storage,
    private readonly injector: EnvironmentInjector
  ) {}

  private static sessionKey(storagePath: string): string {
    return storagePath.replace(/[^a-zA-Z0-9/_\-]/g, '_');
  }

  getDownloadUrl(storagePath: string): Promise<string> {
    const cached = this.memory.get(storagePath);
    if (cached !== undefined) {
      return Promise.resolve(cached);
    }

    const sKey = this.sessionKeyPrefix + StorageUrlCacheService.sessionKey(storagePath);
    try {
      const fromSession = sessionStorage.getItem(sKey);
      if (fromSession) {
        this.memory.set(storagePath, fromSession);
        return Promise.resolve(fromSession);
      }
    } catch {
      /* private mode / quota */
    }

    const existing = this.inflight.get(storagePath);
    if (existing) {
      return existing;
    }

    const promise = runInInjectionContext(this.injector, () =>
      getDownloadURL(ref(this._storage, storagePath))
    ).then(url => {
      this.memory.set(storagePath, url);
      try {
        sessionStorage.setItem(sKey, url);
      } catch {
        /* quota */
      }
      this.inflight.delete(storagePath);
      return url;
    }).catch(err => {
      this.inflight.delete(storagePath);
      throw err;
    });

    this.inflight.set(storagePath, promise);
    return promise;
  }
}
